import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ShoppingCart,
  Menu,
  Package,
  User,
  LogOut,
  ClipboardList,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { CRM_WORDMARK } from "@/lib/brand";
import { useSite, itemHref } from "@/context/SiteContext";
import { useCategories } from "@/hooks/useCategories";
import { useCategoryLabels } from "@/hooks/useCategoryLabels";
import { cmsText } from "@/lib/cms";

const navLinkClasses = ({ isActive }) =>
  `relative whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
    isActive
      ? "bg-primary/10 text-primary"
      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
  }`;

export const Header = () => {
  const { t, i18n } = useTranslation();
  const { site, menus } = useSite();
  const { account, isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();
  const categorieen = useCategories();
  const categorieLabel = useCategoryLabels(categorieen);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [categorieenOpen, setCategorieenOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success(t("account.loggedOut"));
    navigate("/login");
  };

  const menuLinks = (ids) =>
    (ids || [])
      .map((id) => menus.find((m) => m.id === id))
      .filter(Boolean)
      .flatMap((m) => m.items || [])
      .map((item) => ({ to: itemHref(item), label: cmsText(item.label, i18n.language) }))
      .filter((l) => l.label);

  const cmsPublicLinks = menuLinks(site?.header_menus);
  const publicLinks = cmsPublicLinks.length
    ? cmsPublicLinks
    : [
        { to: "/", label: t("nav.home") },
        { to: "/assortiment", label: t("nav.assortiment") },
      ];
  const cmsShopLinks = menuLinks(site?.klant_menus);
  const shopLinks = cmsShopLinks.length
    ? cmsShopLinks
    : [
        { to: "/producten", label: t("nav.producten") },
        { to: "/mijn-bestellingen", label: t("nav.bestellingen") },
      ];
  const links = isAuthenticated ? [...publicLinks, ...shopLinks] : publicLinks;

  return (
    <header
      className={`sticky top-0 z-40 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-border bg-background/85 shadow-[0_10px_30px_-20px_hsl(30_20%_20%/0.5)] backdrop-blur-xl"
          : "border-transparent bg-background/60 backdrop-blur"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[68px] max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2"
          data-testid="header-logo-link"
        >
          <img
            src={site?.logo_url || CRM_WORDMARK}
            alt={t("brand.name")}
            width="201"
            height="28"
            className="h-7 w-auto shrink-0"
            data-testid="header-wordmark"
          />
          <span className="hidden rounded-full border border-primary/30 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-primary sm:block">
            {t("brand.suffix")}
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex" aria-label={t("nav.menu")}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={navLinkClasses}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace(/\//g, "")}`}
            >
              {l.label}
            </NavLink>
          ))}
          {isAuthenticated && categorieen.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 rounded-full px-3.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  data-testid="header-categories-menu"
                >
                  {t("nav.categories")}
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 rounded-xl p-1.5">
                <DropdownMenuItem asChild data-testid="header-category-all">
                  <Link to="/producten">{t("catalog.allCategories")}</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {categorieen.map((naam) => (
                  <DropdownMenuItem key={naam} asChild data-testid={`header-category-${naam.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Link to={`/producten?categorie=${encodeURIComponent(naam)}`}>{categorieLabel(naam)}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild data-testid="header-category-overige">
                  <Link to="/producten?categorie=__overige__">{t("catalog.otherCategory")}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>

          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="hidden max-w-[220px] sm:inline-flex"
                  data-testid="header-account-menu"
                >
                  <User className="mr-2 h-4 w-4" aria-hidden="true" />
                  <span className="truncate">
                    {account?.bedrijfsnaam || account?.email || t("nav.account")}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5">
                <DropdownMenuLabel className="truncate">
                  {account?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-account-link">
                  <Link to="/account">
                    <User className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("nav.account")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-orders-link">
                  <Link to="/mijn-bestellingen">
                    <ClipboardList className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("nav.bestellingen")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="menu-logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Button variant="outline" asChild data-testid="header-login-button">
                <Link to="/login">{t("nav.login")}</Link>
              </Button>
              <Button asChild data-testid="header-register-button">
                <Link to="/registreren">{t("nav.register")}</Link>
              </Button>
            </div>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="lg:hidden"
                aria-label={t("nav.openMenu")}
                data-testid="header-mobile-menu-button"
              >
                <Menu className="h-4 w-4" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="flex w-[300px] flex-col overflow-y-auto bg-card">
              <SheetHeader>
                <SheetTitle>{t("nav.menu")}</SheetTitle>
                <SheetDescription className="sr-only">
                  {t("nav.menu")}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `rounded-md px-3 py-2 text-sm font-medium ${
                        isActive
                          ? "bg-secondary text-primary"
                          : "text-muted-foreground hover:bg-secondary"
                      }`
                    }
                    data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.replace(/\//g, "")}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
              {isAuthenticated && categorieen.length > 0 && (
                <>
                  <Separator className="my-5" />
                  <button
                    type="button"
                    onClick={() => setCategorieenOpen((v) => !v)}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-secondary"
                    aria-expanded={categorieenOpen}
                    data-testid="mobile-categories-toggle"
                  >
                    {t("nav.categories")}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${categorieenOpen ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                  {categorieenOpen && (
                    <div className="mt-2 flex max-h-64 flex-col gap-1 overflow-y-auto" data-testid="mobile-categories-list">
                      {[...categorieen.map((n) => ({ naam: categorieLabel(n), waarde: n })), { naam: t("catalog.otherCategory"), waarde: "__overige__" }].map((c) => (
                        <Link
                          key={c.waarde}
                          to={`/producten?categorie=${encodeURIComponent(c.waarde)}`}
                          onClick={() => setOpen(false)}
                          className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
                          data-testid={`mobile-category-${c.waarde.toLowerCase().replace(/\s+/g, "-").replace(/__/g, "")}`}
                        >
                          {c.naam}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
              <Separator className="my-5" />
              <div className="flex flex-col gap-3">
                <LanguageSwitcher testId="mobile-language-switcher" className="w-full" />
                {isAuthenticated ? (
                  <>
                    <Button
                      variant="outline"
                      asChild
                      onClick={() => setOpen(false)}
                      data-testid="mobile-cart-link"
                    >
                      <Link to="/mandje">
                        <ShoppingCart className="mr-2 h-4 w-4" aria-hidden="true" />
                        {t("nav.mandje")} ({itemCount})
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      asChild
                      onClick={() => setOpen(false)}
                      data-testid="mobile-account-link"
                    >
                      <Link to="/account">
                        <User className="mr-2 h-4 w-4" aria-hidden="true" />
                        {t("nav.account")}
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setOpen(false);
                        handleLogout();
                      }}
                      data-testid="mobile-logout-button"
                    >
                      <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t("nav.logout")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      asChild
                      onClick={() => setOpen(false)}
                      data-testid="mobile-login-button"
                    >
                      <Link to="/login">{t("nav.login")}</Link>
                    </Button>
                    <Button
                      asChild
                      onClick={() => setOpen(false)}
                      data-testid="mobile-register-button"
                    >
                      <Link to="/registreren">
                        <Package className="mr-2 h-4 w-4" aria-hidden="true" />
                        {t("nav.register")}
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {isAuthenticated && (
            <Button
              variant="outline"
              size="icon"
              className="relative"
              aria-label={t("nav.cart")}
              onClick={() => navigate("/mandje")}
              data-testid="header-cart-button"
            >
              <ShoppingCart className="h-4 w-4" aria-hidden="true" />
              {itemCount > 0 && (
                <span
                  className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground"
                  data-testid="header-cart-count"
                >
                  {itemCount}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
